import * as THREE from "three";

const VERT = /* glsl */ `
varying vec3 vDir;
void main() {
  vDir = position;
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mvPosition;
}
`;

const FRAG = /* glsl */ `
precision highp float;
varying vec3 vDir;
uniform float uTime;
uniform vec3 uShipPos;
uniform vec3 uColorA;
uniform vec3 uColorB;
uniform vec3 uColorC;

// hash + value noise
float hash(vec3 p) {
  p = fract(p * 0.3183099 + vec3(0.71, 0.113, 0.419));
  p *= 17.0;
  return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
}
float noise(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(mix(hash(i + vec3(0,0,0)), hash(i + vec3(1,0,0)), f.x),
        mix(hash(i + vec3(0,1,0)), hash(i + vec3(1,1,0)), f.x), f.y),
    mix(mix(hash(i + vec3(0,0,1)), hash(i + vec3(1,0,1)), f.x),
        mix(hash(i + vec3(0,1,1)), hash(i + vec3(1,1,1)), f.x), f.y),
    f.z
  );
}
float fbm(vec3 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise(p);
    p *= 2.07;
    a *= 0.5;
  }
  return v;
}

void main() {
  vec3 d = normalize(vDir);
  float t = uTime * 0.04;
  // Parallax: shift FBM sample by ship position so the nebula visibly
  // streams past as the ship flies forward. 0.0009 keeps it slow — over
  // a 30s run at -28 u/s forward, the noise sample shifts ~0.75 units
  // (half a noise period), which reads as ambient motion not chaos.
  vec3 q = d * 1.6 + vec3(t, t * 0.7, -t * 0.5) + uShipPos * 0.0009;
  float n = fbm(q);
  float n2 = fbm(q * 2.3 + 11.0);

  // Vertical gradient anchor
  float v = clamp(d.y * 0.5 + 0.5, 0.0, 1.0);

  vec3 base = mix(uColorA, uColorB, v);
  vec3 hot = mix(base, uColorC, smoothstep(0.45, 0.85, n) * 0.85);
  vec3 col = hot + vec3(0.02, 0.04, 0.08) * n2;

  // Stars — sparse, depth-cued
  float s = pow(hash(floor(d * 480.0)), 320.0);
  col += vec3(s) * 1.6;

  // gentle vignette toward zenith/nadir
  col *= 1.0 - pow(abs(d.y), 3.0) * 0.4;

  gl_FragColor = vec4(col, 1.0);
}
`;

export class Nebula {
  readonly mesh: THREE.Mesh;
  private uniforms: { uTime: { value: number }; uShipPos: { value: THREE.Vector3 }; uColorA: { value: THREE.Color }; uColorB: { value: THREE.Color }; uColorC: { value: THREE.Color } };

  constructor() {
    this.uniforms = {
      uTime: { value: 0 },
      uShipPos: { value: new THREE.Vector3() },
      uColorA: { value: new THREE.Color("#03040a") },
      uColorB: { value: new THREE.Color("#0a0e22") },
      uColorC: { value: new THREE.Color("#5a1e7a") },
    };
    const mat = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: VERT,
      fragmentShader: FRAG,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
    });
    this.mesh = new THREE.Mesh(new THREE.BoxGeometry(600, 600, 600), mat);
  }

  update(t: number, shipPos?: THREE.Vector3) {
    this.uniforms.uTime.value = t;
    if (shipPos) this.uniforms.uShipPos.value.copy(shipPos);
  }

  setPalette(a: THREE.Color, b: THREE.Color, c: THREE.Color) {
    this.uniforms.uColorA.value.copy(a);
    this.uniforms.uColorB.value.copy(b);
    this.uniforms.uColorC.value.copy(c);
  }
}
